// filepath: apps/web/src/definitions/types.ts
//
// TypeScript mirrors of the Go wire contract at
// internal/form/handlers.go:257-262 (GetFormDefinitionResponse).
//
// Any drift between this file and the Go struct is a CONTRACT VIOLATION.
// CI should fail if the types drift (see 02-impact-analysis.md §10.3).

/**
 * One of the 8 base field types emitted by FR-FORM-1, plus `hidden` for
 * FR-FORM-4 server-evaluated visibility output and `text` (legacy alias
 * accepted by the backend's validTypes map per ISS-038/D-2).
 *
 * Renderer extensions (`select`, `multi-select`, `file`, `computed`)
 * are additive: register a new component in FieldRegistry, no type
 * change required.
 */
export type FieldType =
  | "string"
  | "number"
  | "integer"
  | "boolean"
  | "date"
  | "datetime"
  | "enum"
  | "reference"
  | "hidden"
  | "text";

/**
 * Metadata for a single field within a form definition.
 *
 * Wire format mirrors Go's `FieldMetadata` decoder at
 * internal/form/db_resolver.go:217-220:
 *   { fieldType: string, visibleWhen?: string, computedFrom?: string }
 *
 * ISS-038: the wire format does NOT carry an `enumValues` array. EnumField
 * falls back to free-text + console.warn until the backend adds an
 * `enumValues` field to FieldMetadata (option a in ISS-038 §Fix).
 */
import type { LocaleMap } from "@/i18n/useTranslation";

/**
 * FR-I18N-1 — one option in an enum field with a locale-varying display value.
 * The `value` field is the canonical enum token stored in the DB.
 * The `displayValue` field is the locale-resolved label shown in the UI
 * (server-side pre-resolved when ?locale= is supplied; raw LocaleMap
 * when locale param is absent).
 */
export interface EnumOption {
  value: string;
  /** Locale-resolved display label when server resolved ?locale=;
   * raw LocaleMap when locale param absent (client-side resolution via
   * `resolveLocalizedValue`). */
  displayValue: string | LocaleMap;
}

export interface FieldMetadata {
  fieldType: FieldType | string;
  /** expr-lang visibility formula. Server-evaluated only (ARCH §7.1). */
  visibleWhen?: string;
  /** expr-lang computed-value formula. Server-evaluated only. */
  computedFrom?: string;
  /**
   * Optional UI-side options (placeholder for option b in ISS-038).
   * Most commonly supplied via `ui_schema.fields[id].options`.
   */
  options?: string[];
  /**
   * FR-I18N-1 — locale-varying field label: {locale: value} map.
   * When GET /api/form-definitions/{id}?locale=<locale> is called,
   * the server pre-resolves this to a plain string before JSON serialization.
   * When no locale param is supplied, the raw LocaleMap is returned and
   * the SPA resolves it client-side via `resolveLocalizedValue`.
   */
  label?: string | LocaleMap;
  /**
   * FR-I18N-1 — locale-varying validation error message: {locale: value} map.
   * Displayed when a required field is empty or fails a formula constraint.
   * Same resolution semantics as `label` above.
   */
  validationMessage?: string | LocaleMap;
  /**
   * FR-I18N-1 — locale-varying enum options for enum fields.
   * Each option carries a locale-varying displayValue.
   * Same resolution semantics as `label` above.
   */
  enumOptions?: EnumOption[];
  /**
   * FR-UI-FORM-1 AC-2 — for a reference field, the target entity type
   * id (e.g. "customer"). Consumed by ReferenceField → ReferencePicker
   * to issue GET /api/entity-records/{referenceEntityType} for the
   * option list. Backwards-compatible: a reference field WITHOUT this
   * field renders as a disabled text input showing the stored id
   * (graceful degradation, mirrors the pre-FR-UI-FORM-1 fallback).
   *
   * The field is OPTIONAL so old definitions that pre-date this FR
   * continue to render via the existing text-input stub.
   */
  referenceEntityType?: string;
  /**
   * FR-UI-FORM-1 AC-2 — for a reference field, the field id on the
   * target entity to display in the picker (e.g. "name"). When
   * absent, the picker falls back to the entity's primary sort
   * field (server-side default; sent as `sort=name:asc` only when
   * the definition declares a value).
   */
  referenceDisplayField?: string;
}

/**
 * The raw definition JSON as returned by GET /api/form-definitions/{form_id}.
 * Field IDs are the keys; values are field metadata. An optional top-level
 * `entityType` key is present iff the backend stored one.
 */
export interface FormDefinitionJSON {
  entityType?: string;
  [fieldId: string]: FieldMetadata | string | undefined;
}

/**
 * Optional UI schema sub-document. Present iff the backend stored one
 * (handlers.go:261 — `omitempty`). When absent, the renderer falls back
 * to fieldType-based defaults.
 *
 * FR-UI-CORE-9: the per-field `label` / `help` keys are typed as
 * `string | LocaleMap` (where `LocaleMap` is the FR-I18N-1 contract
 * shape `{ ru?: string; kk?: string; en?: string }`) so definitions
 * can carry either a locale-agnostic label or a per-locale map. The
 * `required` key is a per-field boolean that adds the
 * `aria-required` semantic + visible required-marker to the
 * renderer. All three keys are OPTIONAL — old definitions that
 * pre-date this FR continue to render via the fieldId-as-label
 * fallback (graceful degradation per Rule 7 version-pinning).
 *
 * The `options` key (existing) is preserved; EnumField reads it
 * for the ISS-038 option (b) workaround.
 */
export interface UiSchemaField {
  options?: string[];
  label?: string | LocaleMap;
  help?: string | LocaleMap;
  required?: boolean;
}

export interface UiSchema {
  layout?: string;
  order?: string[];
  fields?: Record<string, UiSchemaField>;
}

/**
 * The exact wire shape returned by GET /api/form-definitions/{form_id}.
 * MUST stay in sync with internal/form/handlers.go:GetFormDefinitionResponse.
 */
export interface GetFormDefinitionResponse {
  form_id: string;
  version: number;
  definition: FormDefinitionJSON;
  ui_schema?: UiSchema | null;
}

/**
 * A versioned key used to look up a definition in the runtime cache.
 *
 * `version` is either a positive integer (pinned, FR-BPM-10) or the literal
 * string 'active' (latest — for admin/builder views only, NEVER for
 * in-flight task interaction per ARCHITECTURE_WEB.md §4).
 *
 * `type` discriminates the kind of definition to fetch. The SPA supports
 * three definition kinds (FR-UI-CORE-3):
 *   - 'form'    — form definition, endpoint `/form-definitions/{id}`
 *   - 'process' — process definition, endpoint `/process-definitions/{id}`
 *                 (consumed by FR-UI-PROC-1; the cache key + URL dispatcher
 *                 are wired here in advance)
 *   - 'entity'  — entity definition, endpoint `/entity-definitions/{id}`
 *                 (consumed by FR-UI-ENT-1; same as above)
 */
export interface DefinitionKey {
  type: "form" | "process" | "entity";
  id: string;
  version: number | "active";
}

/**
 * Runtime fetch error with classification metadata.
 *
 * - `status`: HTTP status code (0 for network failures with no response)
 * - `retryAfterSeconds`: value of `Retry-After` header (rate-limit only)
 * - `code`: backend-supplied error code (e.g. `not_found`, `forbidden`)
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    status: number,
    code: string,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * HTTP 409 conflict response (FR-ENT-8). Carries the server's current
 * `row_version` and (when available) the server's current record body so
 * the SPA can present a conflict-resolution flow (FR-UI-CORE-6).
 *
 * INV-2 / FR-AUTH-7: the server has already stripped any field the caller
 * may not read BEFORE constructing the 409 response. The renderer must
 * never re-introduce such a field from the local pending edit — the
 * `currentRecord` map is authoritative for what fields are addressable.
 *
 * Graceful degradation: both `currentRowVersion` and `currentRecord` are
 * typed as nullable because the backend may omit either (older FR-ENT-8
 * implementations supplied only the status code). The SPA degrades to
 * "Refetch latest" only in that case (no diff context rendered).
 */
export class HttpConflictError extends ApiError {
  readonly currentRowVersion: number | null;
  readonly currentRecord: Record<string, unknown> | null;

  constructor(
    message: string,
    status: number,
    code: string,
    currentRowVersion: number | null,
    currentRecord: Record<string, unknown> | null,
  ) {
    super(message, status, code);
    this.name = "HttpConflictError";
    this.currentRowVersion = currentRowVersion;
    this.currentRecord = currentRecord;
  }
}

/**
 * FR-UI-CORE-10 AC-5 — per-field validation error.
 *
 * The server's 422 envelope is
 *   { errors: { [fieldId]: { code: string, message: string | {locale: value} } } }
 * The `code` is a machine-readable error class (e.g. "required",
 * "min_length", "invalid_format"). The `message` is either a plain
 * string (used as-is for the active locale) or a `{ru?, kk?, en?}`
 * map (FR-I18N-1 contract shape).
 */
export interface FieldErrorBody {
  code: string;
  message: string | LocaleMap;
}

/**
 * FR-UI-CORE-10 AC-5 — per-field error map keyed by fieldId.
 *
 * Used by:
 *   - useFormSubmit to throw an `HttpValidationError` with the parsed map.
 *   - FormRenderer to render `<InlineFieldError>` per field with errors.
 *   - The FormRenderer per-field slot to apply `aria-invalid` +
 *     `aria-describedby` on the matching field input.
 */
export type FieldErrorMap = Record<string, FieldErrorBody>;

/**
 * FR-UI-CORE-10 AC-5 — HTTP 422 validation response.
 *
 * Carries the parsed per-field error map (`{fieldId: {code, message}}`)
 * so the FormRenderer can route each id to its matching `<InlineFieldError>`
 * slot. INV-2 / FR-AUTH-7: the FormRenderer's server-stripping filter
 * MUST drop any error whose fieldId is not in the rendered field set —
 * the server's wire payload is authoritative for which fields are
 * addressable, but a misbehaving backend may return errors for stripped
 * fields and the SPA must not leak their existence.
 *
 * Graceful degradation: `errors` may be empty / missing (older
 * deployments that returned only a 422 status). The FormRenderer treats
 * an empty map as a generic validation failure and routes through
 * `onSubmitError` rather than the per-field slot.
 */
export class HttpValidationError extends ApiError {
  readonly errors: FieldErrorMap;

  constructor(
    message: string,
    status: number,
    code: string,
    errors: FieldErrorMap,
  ) {
    super(message, status, code);
    this.name = "HttpValidationError";
    this.errors = errors;
  }
}

/**
 * Renderer-visible classification of fetch result state. Maps 1:1 to
 * the 5 mandatory error states per ARCHITECTURE_WEB.md §5 +
 * react-conventions.instructions.md §1.
 */
export type RendererStatus =
  | "loading"
  | "success"
  | "fetch-failure"
  | "permission-denied"
  | "stale-version"
  | "rate-limit";

// Re-exported for cross-module consumers (form-renderer field
// components import it via `@/definitions/types`). The canonical
// declaration lives at `@/i18n/useTranslation.ts`.
export type { LocaleMap } from "@/i18n/useTranslation";
